"use client";
import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useCookieConsent } from "@/contexts/cookie-consent-context";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export default function GTMClient() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { consent, hasConsent } = useCookieConsent();

  useEffect(() => {
    if (typeof window === "undefined") return;
    
    // Inizializza dataLayer se non esiste
    window.dataLayer = window.dataLayer || [];
    
    // Invia consent mode a GTM (Google Consent Mode v2)
    if (hasConsent) {
      window.dataLayer.push({
        event: "consent_update",
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.marketing ? "granted" : "denied",
        functionality_storage: consent.functional ? "granted" : "denied",
        personalization_storage: consent.functional ? "granted" : "denied",
        security_storage: "granted", // Sempre garantito per cookie necessari
      });
    } else {
      // Default: nega tutto tranne cookie necessari (prima che l'utente scelga)
      window.dataLayer.push({
        event: "consent_default",
        analytics_storage: "denied",
        ad_storage: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",
      });
    }
    
    // Log per debug
    if (process.env.NODE_ENV === "development") {
      console.log("🍪 Cookie Consent Status:", {
        hasConsent,
        analytics: consent.analytics,
        marketing: consent.marketing,
        functional: consent.functional,
      });
    }
  }, [consent, hasConsent]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    
    // Solo se abbiamo il consenso per analytics, invia pageview
    if (!consent.analytics) {
      if (process.env.NODE_ENV === "development") {
        console.log("⚠️ GTM Pageview bloccato - consenso analytics non dato");
      }
      return;
    }
    
    // Inizializza dataLayer se non esiste
    window.dataLayer = window.dataLayer || [];
    
    const query = searchParams ? searchParams.toString() : "";
    const page = pathname + (query ? `?${query}` : "");
    
    // Push evento pageview con più dettagli per GTM
    window.dataLayer.push({
      event: "pageview",
      page: page,
      path: pathname,
      url: window.location.href,
      title: document.title,
    });
    
    // Log per debug
    if (process.env.NODE_ENV === "development") {
      console.log("📊 GTM Pageview:", { page, pathname, url: window.location.href });
    }
  }, [pathname, searchParams, consent.analytics]);

  return null;
}
